import styled, { css } from 'styled-components';
import { theme } from '@/styles/theme';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const buttonVariants = {
  primary: css`
    background: ${theme.colors.primary.royalBlue};
    color: ${theme.colors.neutral.white};
    border: 2px solid ${theme.colors.primary.royalBlue};
    box-shadow: ${theme.shadows.sm};

    &:hover:not(:disabled) {
      background: ${theme.colors.primary.navy};
      border-color: ${theme.colors.primary.navy};
      box-shadow: ${theme.shadows.md};
    }
  `,
  secondary: css`
    background: ${theme.colors.primary.gold};
    color: ${theme.colors.primary.navy};
    border: 2px solid ${theme.colors.primary.gold};
    font-weight: ${theme.fontWeights.bold};
    box-shadow: ${theme.shadows.sm};

    &:hover:not(:disabled) {
      background: #c9a647;
      border-color: #c9a647;
      box-shadow: ${theme.shadows.md};
    }

    &:active:not(:disabled) {
      box-shadow: ${theme.shadows.sm};
    }
  `,
  // For use on DARK backgrounds only (hero sections). On light backgrounds, use variant="ghost" instead.
  outline: css`
    background: rgba(16, 19, 31, 0.2);
    color: ${theme.colors.neutral.white};
    border: 2px solid rgba(255, 255, 255, 0.55);

    /* Fill to ivory with navy ink: crisp and immediate, and distinct from the
       gold secondary button that sits beside it in the hero. */
    &:hover:not(:disabled) {
      color: ${theme.colors.primary.navy};
      border-color: ${theme.colors.secondary.ivory};
      background: ${theme.colors.secondary.ivory};
      box-shadow: 0 6px 24px rgba(8, 10, 18, 0.35);
    }
  `,
  ghost: css`
    background: transparent;
    color: ${theme.colors.primary.royalBlue};
    border: 2px solid transparent;

    &:hover:not(:disabled) {
      color: ${theme.colors.primary.navy};
      background: rgba(15, 52, 96, 0.06);
    }
  `,
};

const buttonSizes = {
  sm: css`
    padding: ${theme.spacing[2]} ${theme.spacing[4]};
    font-size: ${theme.fontSizes.sm};
    gap: ${theme.spacing[1]};
  `,
  md: css`
    padding: ${theme.spacing[3]} ${theme.spacing[6]};
    font-size: ${theme.fontSizes.base};
    gap: ${theme.spacing[2]};
  `,
  lg: css`
    padding: ${theme.spacing[4]} ${theme.spacing[10]};
    font-size: ${theme.fontSizes.lg};
    gap: ${theme.spacing[2]};
  `,
};

export const Button = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.fonts.body};
  font-weight: ${theme.fontWeights.semibold};
  border-radius: ${theme.borderRadius.xl};
  cursor: pointer;
  /* Only the painted properties, and fast: a 700ms "all" transition made every
     hover and press feel laggy. */
  transition:
    background-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 180ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  white-space: nowrap;
  letter-spacing: 0.02em;

  ${({ variant = 'primary' }) => buttonVariants[variant]}
  ${({ size = 'md' }) => buttonSizes[size]}
  ${({ fullWidth }) => fullWidth && css`width: 100%;`}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    filter: grayscale(0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(0.98);
  }

  /* Icon alignment */
  svg {
    position: relative;
    z-index: 1;
    transition: transform ${theme.transitions.base};
  }

  &:hover:not(:disabled) svg {
    transform: scale(1.1);
  }

  /* Content wrapper to ensure text is above pseudo-elements */
  & > * {
    position: relative;
    z-index: 1;
  }
`;
